



























































































































































































































































import { AxiosError } from 'axios';
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep, truncate } from 'lodash';
import moment from 'moment';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';

import {
  IAttachment,
  ITimeOffRequest,
  IMentionAttachmentComment,
  TimeOffRequestComment,
  TimeOffRequestCommentAttachment,
  TimeOffRequestCommentFieldError
} from '@/models';
import { ErrorManager, ISystemError } from '@/models/error';

import {
  createFile,
  diffDeep,
  isHtmlTextEmpty,
  setErrorFromAxiosError
} from '@/utils/helpers';

import CommentAttachmentList from '../common/CommentAttachmentList.vue';
import QuillEditor from '../common/QuillEditor.vue';
import MentionProvider from '../mention/MentionProvider.vue';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog.vue';

export default mixins(auth, snackbar).extend({
  name: 'TimeOffRequestCommentListItem',

  components: {
    CommentAttachmentList,
    DeleteConfirmationDialog,
    QuillEditor,
    MentionProvider
  },

  props: {
    timeOffRequest: {
      type: Object as PropType<ITimeOffRequest>,
      required: true
    },

    timeOffRequestComment: {
      type: Object as PropType<IMentionAttachmentComment>,
      required: true
    }
  },

  data() {
    return {
      isEditing: false,

      commentLoading: false,
      fieldError: new TimeOffRequestCommentFieldError(),

      commentErrorMessage: '',
      commentErrorMessageDetail: '',

      // delete comment
      deleteCommentError: null as ISystemError | null,
      deleteCommentErrorMessageDetail: '',
      commentDeleteInProgress: false,
      selectedTimeOffRequestComment: cloneDeep(this.timeOffRequestComment),

      deleteTimeOffRequestCommentDialog: false,

      commentTextHtml: this.timeOffRequestComment.getTextHtml(),
      mentionsLoading: false,

      deleteCommentAttachmentDialog: false,
      isAttachmentDeleteInProgress: false,
      attachmentDeleteError: null as ISystemError | null,
      selectedAttachment: null as null | IAttachment,
      attachments: [] as IAttachment[]
    };
  },

  computed: {
    ...mapGetters({
      mentionableUsers: 'timeOffRequest/getTimeOffRequestMentionableUsers'
    }),

    isTimeOffRequestCommentOwner(): boolean {
      return this.getCurrentUserFullName === this.timeOffRequestComment.owner;
    },

    canSaveComment(): boolean {
      return (
        !isHtmlTextEmpty(this.selectedTimeOffRequestComment.text) &&
        !this.commentLoading
      );
    },

    textHasDifference(): boolean {
      return (
        this.selectedTimeOffRequestComment.text ===
        this.timeOffRequestComment.text
      );
    }
  },

  methods: {
    ...mapActions({
      updateTimeOffRequestComment: 'timeOffRequest/updateTimeOffRequestComment',
      deleteTimeOffRequestComment: 'timeOffRequest/deleteTimeOffRequestComment',
      addTimeOffRequestCommentAttachments:
        'timeOffRequest/addTimeOffRequestCommentAttachments',
      removeTimeOffRequestCommentAttachment:
        'timeOffRequest/removeTimeOffRequestCommentAttachment',
      fetchTimeOffRequestCommentsMentionableUsers:
        'timeOffRequest/fetchTimeOffRequestCommentsMentionableUsers'
    }),

    truncateFileName(filename: string) {
      const extension = filename.split('.').pop()!;

      return `${truncate(filename, {
        length: 10,
        omission: '[...]'
      })}${extension}`;
    },

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new TimeOffRequestCommentAttachment({ file: file })
        )
      );
    },

    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    getInitials(name: string) {
      return name
        .toUpperCase()
        .split(' ')
        .map(n => n[0])
        .join('');
    },

    startTimeOffEditingComment() {
      // To reset the selected timeOffRequest comment data to the original when rediting
      this.selectedTimeOffRequestComment = cloneDeep(
        this.timeOffRequestComment
      );
      this.commentTextHtml = this.timeOffRequestComment.getTextHtml();
      this.isEditing = true;
    },

    stopEditingComment() {
      if (!this.selectedTimeOffRequestComment.text || this.textHasDifference) {
        this.isEditing = false;
      }
    },

    clearAllErrors(): void {
      this.fieldError.clearErrors();
      this.deleteCommentError = null;
      this.deleteCommentErrorMessageDetail = '';
      this.commentErrorMessage = '';
      this.commentErrorMessageDetail = '';
      this.attachmentDeleteError = null;
    },

    closeTimeOffRequestCommentDeleteDialog() {
      this.clearAllErrors();
      this.deleteTimeOffRequestCommentDialog = false;
    },

    removeTimeOffRequestComment(comment: IMentionAttachmentComment) {
      this.clearAllErrors();
      this.commentDeleteInProgress = true;
      this.deleteTimeOffRequestComment({
        timeOffId: this.timeOffRequest.id,
        commentId: comment.id
      })
        .then(() => {
          this.closeTimeOffRequestCommentDeleteDialog();
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail) {
              this.deleteCommentErrorMessageDetail = error.response.data.detail;
            }
          }
          this.deleteCommentError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.commentDeleteInProgress = false;
        });
    },

    editComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = TimeOffRequestComment.parseHtmlToMentionText(
        this.commentTextHtml
      );

      this.selectedTimeOffRequestComment.text = parsedCommentMentionText;

      const commentDelta = diffDeep(
        this.selectedTimeOffRequestComment,
        this.timeOffRequestComment,
        true
      );

      return this.updateTimeOffRequestComment({
        timeOffId: this.timeOffRequest.id,
        comment: commentDelta
      })
        .then(async () => {
          if (this.attachments.length !== 0) {
            await this.addTimeOffRequestCommentAttachments({
              timeOffId: this.timeOffRequest.id,
              commentId: commentDelta.id,
              attachments: this.attachments
            });
            this.attachments = [];
          }
          this.selectedTimeOffRequestComment = cloneDeep(
            this.timeOffRequestComment
          );
          this.isEditing = false;
        })
        .catch(error => {
          if (error.response) {
            // client received an error response that falls out of range 2xx
            if (
              TimeOffRequestCommentFieldError.isTimeOffRequestCommentFieldError(
                error.response.data
              )
            ) {
              this.fieldError = new TimeOffRequestCommentFieldError(
                error.response.data
              );
            }

            if (error.response.data.detail) {
              this.commentErrorMessageDetail = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchTimeOffRequestCommentsMentionableUsers(
          this.timeOffRequestComment.id
        );
      } catch (error) {
        this.commentErrorMessage = setErrorFromAxiosError(
          error as AxiosError
        ).message;
      } finally {
        this.mentionsLoading = false;
      }
    },

    downloadAttachmentFile(attachment: IAttachment) {
      this.clearAllErrors();
      this.$services.fileLoader
        .downloadFile(attachment.file as string)
        .then(data => {
          createFile(data, attachment.filename);

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: `File attachment download successfully`,
            btnText: 'Ok',
            timeout: 6000
          });
        })
        .catch(error => {
          const err = setErrorFromAxiosError(error);
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Failed to download Attachment: ${err.detail || err.message}`,
            btnText: 'Close'
          });
        });
    },

    openAttachmentDeleteDialog(attachment: IAttachment) {
      this.clearAllErrors();
      this.selectedAttachment = attachment;
      this.deleteCommentAttachmentDialog = true;
    },

    cancelDeleteCommentAttachmentDialog() {
      this.deleteCommentAttachmentDialog = false;
    },

    removeCommentAttachment(attachment: IAttachment) {
      this.clearAllErrors();
      this.isAttachmentDeleteInProgress = true;

      this.removeTimeOffRequestCommentAttachment({
        timeOffId: this.timeOffRequest.id,
        commentId: this.timeOffRequestComment.id,
        attachmentId: attachment.id
      })
        .then(() => {
          this.cancelDeleteCommentAttachmentDialog();
        })
        .catch(error => {
          this.attachmentDeleteError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.isAttachmentDeleteInProgress = false;
        });
    }
  }
});
